import React from 'react';
import { Box, Typography, Paper, useTheme, Divider } from '@mui/material';
import { ProcessStep, StageDetails as StageDetailsType } from '../../types/processFlowTypes';

interface StageDetailsProps {
  stageDetails?: StageDetailsType;
  currentStage: string;
  selectedNodeId?: string; // New prop to track the selected node
  selectedStepObject?: ProcessStep; 
}

const StageDetails: React.FC<StageDetailsProps> = ({
  stageDetails,
  currentStage,
  selectedNodeId,
  selectedStepObject
}) => {
  const theme = useTheme();

  if (!stageDetails) {
    return null;
  }

  // Find the selected step from the current stage's steps with improved lookup across stages if needed
  const selectedStep = React.useMemo(() => {
    console.log('Looking for node ID:', selectedNodeId, 'in stage:', currentStage);
    
    if (!selectedNodeId) {
      console.log('No node ID selected, showing first step of stage');
      return stageDetails.steps[0]; // Default to first step when no specific node is selected
    }
    
    // Try to find the step in the current stage
    const step = stageDetails.steps.find(step => step.id === selectedNodeId);
    
    if (step) {
      console.log('Found step in current stage:', step);
      return step;
    } else {
      console.warn(`Step with ID ${selectedNodeId} not found in current stage ${currentStage}`);
      
      // If not found in current stage, show first step
      return stageDetails.steps[0];
    }
  }, [selectedNodeId, stageDetails, currentStage, selectedStepObject]);
  
  console.log('Final Selected Step:', selectedStep);

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        p: 2, 
        height: '100%',
        minHeight: 400,
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Typography 
        variant="h6" 
        gutterBottom 
        sx={{ 
          borderLeft: `4px solid ${theme.palette.primary.main}`, 
          pl: 2 
        }}
      >
        {stageDetails.title || 'Stage Details'}
      </Typography>
      
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Typography variant="body1" paragraph sx={{ pl: 2, pr: 2 }}>
          {stageDetails.description}
        </Typography>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ pl: 2, pr: 2 }}>
          {selectedStepObject ? (
            <>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: theme.palette.primary.main }}>
                {selectedStepObject.label}
              </Typography>
              
              <Paper 
                elevation={0}
                variant="outlined"
                sx={{ 
                  p: 2, 
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  mb: 2
                }}
              >                
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 2
                }}>
                  <Box>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      backgroundColor: 'rgba(33, 150, 243, 0.04)', 
                      p: 1.5, 
                      borderRadius: 1,
                      borderLeft: `3px solid ${theme.palette.primary.light}`
                    }}>
                      {selectedStepObject.description}
                    </Typography>
                  </Box>
                  
                  {selectedStepObject.instructions && (
                    <Box>
                      <Typography variant="subtitle2" color="secondary" gutterBottom>
                        Instructions
                      </Typography>
                      <Box sx={{ 
                        backgroundColor: 'rgba(255, 152, 0, 0.04)', 
                        p: 1.5, 
                        borderRadius: 1,
                        borderLeft: `3px solid ${theme.palette.secondary.light}`,
                        position: 'relative'
                      }}>
                        <Typography variant="body2">
                          {selectedStepObject.instructions}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Paper>
            </>
          ) : (
            <Box sx={{ 
              p: 3, 
              textAlign: 'center', 
              border: `1px dashed ${theme.palette.divider}`,
              borderRadius: 1,
              bgcolor: 'rgba(0, 0, 0, 0.01)'
            }}>
              <Typography variant="body2" sx={{ fontStyle: 'italic', color: theme.palette.text.secondary }}>
                Select a step in the diagram to view its details.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default StageDetails;
